import React, {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";
import {useSelector} from "react-redux";
import {formatNumberWithCommas, getFiat} from "../../../../common/utils/function";
import {chartApi, dashboardService} from "../../../../store/actions";
import {CHART_LIST, CONSTANT, RANGE_BUTTON} from "../../../../common/utils/constants";

import HighchartsExporting from "highcharts/modules/exporting";

HighchartsExporting(Highcharts);

interface ChartXDCPriceProps {
}

const ChartXDCPriceDetail: FC<ChartXDCPriceProps> = () => {
    const navigate = useNavigate();
    const stats = useSelector((e: any) => e?.common?.stats);
    // const location = useLocation();
    const params = useParams();
    const [loading, setLoading] = useState(true);

    const chartinfo = useMemo(() => {
        const allCharts = CHART_LIST.flatMap(detail =>
            detail.sections.flatMap(section => section.charts)
        );
        return allCharts.find(chart => chart.id === params?.chartType) || null;
    }, [params]);
    const baseUnit = process.env.REACT_APP_BASE_UNIT || "XDC";

    const [options, setOptions] = useState<any>({
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: "areaspline",
            zoomType: "",
            height: 600,
            // spacingTop: 0,
            spacingLeft: 0,
            spacingRight: 0,
        },
        exporting: {
            enabled: true,
            buttons: {
                contextButton: {
                    menuItems: [
                        'viewFullscreen',
                        'printChart',
                        'separator',
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                        {
                            text: 'Share',
                            onclick: function () {
                                handleShareButton();
                            }
                        }
                    ]
                }
            }
        },
        title: {
            x: 22,
            useHTML: true,
            style: {
                fontSize: '18px',
                fontWeight: 400,
                fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
            },
            text: `<span class="bs-font">${baseUnit} ${chartinfo?.title} </span>`,
            align: "left",
        },
        credits: {enabled: false},
        subtitle: {
            x: 22,
            useHTML: true,
            style: {
                fontSize: '12px',
                lineHeight: 1.5,
                fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
                color: 'var(--bs-font-secondary)',
            },
            text: `Source: ${process.env.REACT_APP_BASE_EXPLORER || "Blocksscan"}.io <br/> Click and drag in the plot area to zoom in`,
            align: "left",
        },
        marker: {
            radius: 2,
        },
        plotOptions: {
            series: {
                color: `#${CONSTANT.CHART_COLOUR}`,
                lineWidth: "2px",
                fillColor: {
                    linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                    stops: [
                        [0, `#${CONSTANT.CHART_COLOUR}66`],
                        [0.6, `#${CONSTANT.CHART_COLOUR}00`],
                        // [1, `#${CONSTANT.CHART_COLOUR}00`],
                    ],
                },
            },
        },
        xAxis: {
            type: "date",
            visible: true,
            title: {
                text: "Date",
            },
            lineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: "transparent",
            minorTickLength: 0,
            labels: {
                style: {
                    fontSize: "11px",
                    fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
                    color: "var(--bs-text-chart)",
                },
                y: 20,
                formatter: function (this: any) {
                    return moment(this.value).format("MMM 'DD") + " <br/>";
                },
                padding: 0,
            },
            tickLength: 0,
        },
        yAxis: {
            opposite: false,
            title: {
                text: chartinfo?.title,
            },
            // lineWidth: 0,
            // gridLineWidth: 0,
            // minorGridLineWidth: 0,
            // lineColor: "transparent",
            labels: {
                style: {
                    fontSize: "11px",
                    fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
                    color: "var(--bs-text-chart)",
                },
                autoRotation: false,
                padding: 0,
            },
            minorTickLength: 0,
            tickLength: 0,
        },
        legend: {
            enabled: false,
        },
        navigator: {
            enabled: false,
        },
        scrollbar: {
            enabled: false,
        },
        rangeSelector: {
            allButtonsEnabled: true,
            buttonPosition: {
                align: "left",
                x: -20,
                y: 0,
            },
            inputEnabled: true,
            inputStyle: {
                color: `#${CONSTANT.CHART_COLOUR}`,
                fontWeight: "lighter",
            },
            inputEditStyle: {
                border: "none",
            },
            inputDateFormat: '%b %e, %Y',
            inputEditDateFormat: '%b %e, %Y',
            buttons: RANGE_BUTTON,
        },
        series: [
            {
                data: [],
                marker: {
                    enabled: false,
                },
            },
        ],
        tooltip: {
            enabled: false,
        },
    }) as any;

    const chartRef = useRef<any>(null);

    useEffect(() => {
        if (chartRef && chartRef.current) {
            const chart = chartRef.current.chart;
            const logoWidth = 100;
            const logoHeight = 30;

            chart.renderer
                .image(
                    `${process.env.REACT_APP_BASE_URL}/eaas/img/${process.env.REACT_APP_BASE_UNIT}-dark-logo.svg`,
                    chart.plotLeft + (chart.plotWidth - logoWidth) / 2,
                    chart.plotTop + (chart.plotHeight - logoHeight) / 2,
                    logoWidth,
                    logoHeight
                )
                .addClass("chart-brand-logo")
                .add();
        }
    }, []);

    const handleShareButton = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Chart Data',
                text: 'Check out this chart!',
                url: window.location.href
            }).catch(err => console.error("Error sharing", err));
        } else {
            alert("Share feature is not supported in this browser.");
        }
    };

    const getPriceChart = async (selectedIndex: number) => {
        try {
            let toDate: string, fromDate: string;

            // Determine the date range based on selectedIndex
            switch (selectedIndex) {
                case 0: // 1 month
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
                case 1: // 3 months
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(3, "months").format("YYYY-MM-DD");
                    break;
                case 2: // 1 year
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "year").format("YYYY-MM-DD");
                    break;
                case 3: // All
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(5, "years").format("YYYY-MM-DD");
                    break;
                default:
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
            }

            const response = await dashboardService.priceChart();
            const data = response.data;
            data.chart_data.sort((a: any, b: any) => {
                const dateA = new Date(a.date).getTime();
                const dateB = new Date(b.date).getTime();
                return dateA - dateB;
            });

            const seriesData = data.chart_data.map((e: any) => [
                moment.utc(e.date).valueOf(),
                parseFloat(e.closing_price),
            ]);

            setOptions((prevOptions: any) => ({
                ...prevOptions,
                series: [
                    {
                        marker: {
                            enabled: true,
                            radius: 2,
                        },
                        data: seriesData,
                    },
                ],
                tooltip: {
                    enabled: true,
                    useHTML: true,
                    formatter: function (this: Highcharts.Point): string {
                        const x = this.x ?? 0;
                        const y = this.y ?? 0;
                        const price = parseFloat(y.toFixed(4));
                        const date = moment(x).format("dddd, MMMM DD, YYYY");
                        return `
                            <table>
                                <tr>
                                    <td style="font-size: 10px;">${date}</td>
                                </tr>
                                <tr>
                                    <td style="font-size: 12px;">[<span style="color: #${CONSTANT.CHART_COLOUR}" >Price : </span> <span style="font-weight: bold; color: black;">$${price}</span>]</td>
                                </tr>
                            </table>`;
                    },
                },
            }));
        } catch (err) {
            console.error("Error fetching price chart data:", err);
        }
    };

    const getDailyTransactionChart = async (selectedIndex: number) => {
        try {
            let toDate, fromDate;
            switch (selectedIndex) {
                case 0: // 1m
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
                case 1: // 3m
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(3, "month").format("YYYY-MM-DD");
                    break;
                case 2: // 1y
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "year").format("YYYY-MM-DD");
                    break;
                case 3: // All
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
                default:
                    break;
            }
            dashboardService
                .chartTransactions()
                .then((response) => {
                    response.chart_data.sort((a: any, b: any) => {
                        const dateA: any = new Date(a.date);
                        const dateB: any = new Date(b.date);
                        return dateA - dateB;
                    });
                    const seriesData = response.chart_data.map((e: any) => [
                        moment.utc(e?.date).valueOf(),
                        parseFloat(e.tx_fees),
                    ]);
                    let opt = {
                        ...options,
                        series: [{marker: {enabled: true, radius: 2,}, data: seriesData}],
                        tooltip: {
                            enabled: true,
                            useHTML: true,
                            formatter: function (this: Highcharts.Point): string {
                                const x = this.x ?? 0;
                                const y = this.y ?? 0;
                                const tx_fees = (y);
                                const date = moment(x).format("dddd, MMMM DD, YYYY");
                                return `
                            <table>
                                <tr>
                                    <td style="font-size: 10px;">${date}</td>
                                </tr>
                                <tr>
                                    <td style="font-size: 12px;">[<span style="color: #${CONSTANT.CHART_COLOUR}" >Transactions Fee : </span> <span style="font-weight: bold; color: black;">$${tx_fees}</span>]</td>
                                </tr>
                            </table>`;
                            },
                        }
                    };
                    setOptions(opt);
                })
                .catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };

    const getChartData = async (selectedIndex: number) => {
        try {
            setLoading(true);
            let toDate, fromDate;
            switch (selectedIndex) {
                case 0: // 1m
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
                case 1: // 3m
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(3, "month").format("YYYY-MM-DD");
                    break;
                case 2: // 1y
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "year").format("YYYY-MM-DD");
                    break;
                case 3: // All
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(3, "month").format("YYYY-MM-DD");
                    break;
                default:
                    toDate = moment().format("YYYY-MM-DD");
                    fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
                    break;
            }
            const url = `${chartApi.defaults.baseURL}/lines/${params?.chartType}?from=${fromDate}&to=${toDate}`;
            await axios.get(url).then((res) => {
                const data = res?.data?.chart ?? res?.data;
                data.sort((a: any, b: any) => {
                    const dateA: any = new Date(a.date);
                    const dateB: any = new Date(b.date);
                    return dateA - dateB;
                });
                const seriesData = data.map((e: any) => [
                    moment.utc(e?.date).valueOf(),
                    parseFloat(e.value),
                ]);
                let opt = {
                    ...options, series: [{marker: {enabled: true, radius: 2,}, data: seriesData}],
                    tooltip: {
                        enabled: true,
                        useHTML: true,
                        formatter: function (this: Highcharts.Point): string {
                            const x = this.x ?? 0;
                            const y = this.y ?? 0;
                            const data = parseFloat(y.toFixed(4));
                            const date = moment(x).format("dddd, MMMM DD, YYYY");
                            return `
                            <table>
                                <tr>
                                    <td style="font-size: 10px;">${date}</td>
                                </tr>
                                <tr>
                                    <td style="font-size: 12px;">[<span style="color: #${CONSTANT.CHART_COLOUR}" >${chartinfo?.title.replace(/\b\w/g, (char: any) => char.toUpperCase())} : </span> <span style="font-weight: bold; color: black;">${formatNumberWithCommas(data.toString())}</span>]</td>
                                </tr>
                            </table>`;
                        },
                    },
                };
                if (params?.chartType === 'contractsGrowth') {
                    const uniqueDates = data.map((e: any) => moment.utc(e.date).format("YYYY-MM-DD"));
                    opt.xAxis = {
                        categories: uniqueDates,
                        labels: {
                            formatter: function (this: any) {
                                return moment(this.value).format("DD MMM") + ' <br/>';
                            },
                        },
                    };
                }
                if (params?.chartType === "newNativeCoinTransfers") {

                }
                if (params?.chartType === 'gasUsedGrowth') {
                    opt.chart.type = "column";
                }
                setOptions(opt);
                setLoading(false);
            });
        } catch (err) {
            console.log(err);
        }
    };

    useLayoutEffect(() => {
        if (params && params?.chartType) filterChartAPICall(3);
    }, [params]);

    const filterChartAPICall = (selectedIndex: any) => {
        if (params && params?.chartType) {
            switch (params.chartType) {
                case "market":
                    getPriceChart(selectedIndex);
                    break;
                case "averageTxnFee":
                    getDailyTransactionChart(selectedIndex);
                    break;
                default:
                    getChartData(selectedIndex);
                    break;
            }
        }
    };

    return (
        <div className="py-3">
            <div className="container">
                <div className="row">
                    <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom gap-3 py-3">
                        <div className="d-flex flex-column gap-2">
                            <a className="small" href="javaScript:void(0)" onClick={() => navigate("/charts")}>
                                <i className="fa fa-arrow-left"></i> Charts & Stats
                            </a>
                            <h1 className="fs-5 mb-2 fw-medium lh-sm bs-font">
                                {`${baseUnit} ${chartinfo?.title}` || `${baseUnit} Chart`}
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container pt-4 pb-5">
                <div className="row">
                    <div className="col-12 mb-5 mb-lg-0 col-lg-8 col-xl-9">
                        <div className="bs-card p-3 mb-4">
                            <div className="bs-card-body" style={{height: "600px", padding: "12px"}}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={options}
                                    constructorType={"stockChart"}
                                    allowChartUpdate={true}
                                    ref={chartRef}
                                    callback={(chart: any) => {
                                        const buttons = chart?.rangeSelector?.buttons;
                                        buttons.forEach((button: any, index: number) => {
                                            button.element.addEventListener("click", () => {
                                                filterChartAPICall(index);
                                            });
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <p className="d-flex align-items-baseline fs-sm text-muted gap-1 mb-3">
                            <i className="fa fa-download me-1"></i>Download:{" "}
                            <a href="/chart/coinprice?output=csv">CSV Data</a> (Attribution Required)
                        </p>
                    </div>
                    <div className="col-lg-4 col-xl-3">
                        <h2 className="title-txt">About</h2>
                        <p>{chartinfo?.description}</p>
                        <hr/>
                        <div>
                            <h6 className="text-cap mb-1">CURRENT {process.env.REACT_APP_BASE_UNIT} PRICE</h6>
                            <p>
                                <span className="fs-5">{getFiat(stats?.coin_price, 5)}</span>
                                <span className="small text-muted">@{getFiat(stats?.coin_price, 10, "BTC")}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChartXDCPriceDetail;
